import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CropChip } from '@/components/ui/crop-chip';
import { ErrorState } from '@/components/ErrorState';
import { api } from '@/lib/api';
import { classifyApiError } from '@/lib/apiError';
import { useScopeChanged } from '@/lib/scopeEvent';
import { cn } from '@/lib/utils';
import { getCropColor } from '@/lib/colorMaps';
import { formatSeasonName } from '@/lib/season/format';

interface RotationItem {
  crop_id: string | null;
  crop_name: string | null;
  local_name: string | null;
  display_name: string | null;
  fields_count: number;
  planned_area_ha: number;
  actual_area_ha: number;
  status_planned: number;
  status_growing: number;
  status_harvested: number;
  status_other: number;
  /** Пар: занимает поле, но не сеется — процент выполнения к нему неприменим. */
  is_fallow?: boolean;
}

interface RotationSummary {
  season_code: string | null;
  season_year: number | null;
  total_planned_ha: number;
  total_actual_ha: number;
  total_fields: number;
  total_fallow_planned_ha?: number;
  total_fallow_actual_ha?: number;
  items: RotationItem[];
}

// Crop colours централизованы в @/lib/colorMaps (domain colormap,
// whitelisted в frontend-vite/CLAUDE.md). getCropColor — substring-match.

function planCompletionBadge(plan: number, fact: number) {
  if (plan <= 0 || fact <= 0) return null;
  const pct = (fact / plan) * 100;
  const variant: 'primary' | 'accent-brand' | 'destructive' =
    pct >= 100 ? 'primary' : pct >= 90 ? 'accent-brand' : 'destructive';
  const sign = pct > 100 ? '+' : '';
  return (
    <Badge variant={variant} className="text-[10px] px-1.5 py-0">
      {sign}
      {Math.round(pct)}%
    </Badge>
  );
}

/**
 * Tabular rotation summary — list of crops with fields/area/status counts.
 * Простая HTML-table вместо AntD Table — без сортировки/пагинации в первом
 * проходе. TODO: virtualization + sortable columns в Phase 2.
 */
export function RotationCard() {
  const [data, setData] = useState<RotationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  // Ошибка сервера/сети должна ВЫГЛЯДЕТЬ как ошибка, а не как «нет данных»
  // (AK-790 re-land #670, UAT 05.08 баг 12: 8× 500 на rotation-summary
  // читались как «Нет посевов», и полтора часа искали дефект в БД). Отдельно
  // от `data`: упавший refetch не сбрасывает прошлую таблицу — плашка ложится
  // ПОВЕРХ неё.
  const [error, setError] = useState<string | null>(null);

  const loadRotation = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<RotationSummary>('/api/v1/dashboard/rotation-summary')
      .then(setData)
      .catch((err) => {
        setError(classifyApiError(err, 'RotationCard.loadRotation').message);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRotation();
  }, [loadRotation]);
  useScopeChanged(loadRotation);

  const items = data?.items ?? [];
  // Итог в шапке = СУММА СТРОК таблицы, включая пар. Иначе человек, сложивший
  // колонку «План, га», получает число больше итога и никакого объяснения
  // разницы на экране нет (вердикт head-web по #829): два верных числа рядом
  // без подписи читаются как «виджет врёт» — ровно тот сюжет, ради которого
  // этот фикс и делается, только с другой стороны.
  const totalPlan = data?.total_planned_ha ?? 0;
  const totalFact = data?.total_actual_ha ?? 0;
  const fallowPlan = data?.total_fallow_planned_ha ?? 0;
  const fallowFact = data?.total_fallow_actual_ha ?? 0;
  // Пар — часть итога, поэтому подписывается «в т.ч.». А вот ПРОЦЕНТ сева
  // считается только по тому, что сеется: пар не засевают, и включать его в
  // знаменатель значит занижать выполнение плана.
  const sowablePlan = Math.max(0, totalPlan - fallowPlan);
  const sowableFact = Math.max(0, totalFact - fallowFact);
  const fallowHa = fallowPlan > 0 ? fallowPlan : fallowFact;
  const fallowIsFactOnly = fallowPlan <= 0 && fallowFact > 0;

  return (
    <Card className="mt-6">
      <CardHeader className="flex-row items-center justify-between space-y-0 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CardTitle>Структура посевных площадей</CardTitle>
          {data?.season_code && (
            <Badge variant="primary">
              {formatSeasonName(data.season_code, data.season_year)}
            </Badge>
          )}
        </div>
        {data && (
          <div className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <span>
              {data.total_fields.toLocaleString('ru-RU')} посевов
            </span>
            <span>·</span>
            <span>
              план{' '}
              <strong className="text-foreground">
                {Math.round(totalPlan).toLocaleString('ru-RU')}
              </strong>{' '}
              га
            </span>
            <span>·</span>
            <span>
              факт{' '}
              <strong className="text-foreground">
                {Math.round(totalFact).toLocaleString('ru-RU')}
              </strong>{' '}
              га
            </span>
            {fallowHa > 0 && (
              <>
                <span>·</span>
                <span data-testid="rotation-total-fallow">
                  в т.ч. под паром{' '}
                  <strong className="text-foreground">
                    {Math.round(fallowHa).toLocaleString('ru-RU')}
                  </strong>{' '}
                  га {fallowIsFactOnly ? 'по факту, ' : ''}не сеется
                </span>
              </>
            )}
            {sowablePlan > 0 && sowableFact > 0 && (
              <>
                <span>·</span>
                {/* Процент — по севу, без пара; подписан, чтобы его не
                    пересчитывали из соседних чисел «в уме» и не спорили. */}
                <span title="Выполнение плана сева — без пара: пар не засевают">
                  {planCompletionBadge(sowablePlan, sowableFact)}
                </span>
              </>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {error && data && (
          // Refetch упал, старые данные лежат — плашка ПОВЕРХ таблицы, цифры
          // прошлого запроса не пропадают, но и не выдаются за свежие.
          <ErrorState
            variant="inline"
            className="mb-3"
            message={error}
            onRetry={loadRotation}
          />
        )}
        {loading && !data ? (
          <div className="text-muted-foreground py-12 text-center">
            Загружаем…
          </div>
        ) : error && !data ? (
          // Данных не было — блокирующая плашка ВМЕСТО «Нет посевов»: пустая
          // таблица прочиталась бы как «данных нет», а не как ошибка сервера.
          <ErrorState message={error} onRetry={loadRotation} />
        ) : items.length === 0 ? (
          <div className="text-muted-foreground py-12 text-center">
            Нет посевов в текущем сезоне
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="text-left py-2 px-3 font-medium">Культура</th>
                  <th className="text-right py-2 px-3 font-medium">Полей</th>
                  <th className="text-right py-2 px-3 font-medium">
                    Площадь (план / факт)
                  </th>
                  <th className="text-left py-2 px-3 font-medium">
                    Доля от общего
                  </th>
                  <th className="text-left py-2 px-3 font-medium">Статусы</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, idx) => {
                  const c = getCropColor(r.display_name);
                  const denom = totalFact > 0 ? totalFact : totalPlan;
                  const base =
                    totalFact > 0 ? r.actual_area_ha : r.planned_area_ha;
                  const pct = denom > 0 ? (base / denom) * 100 : 0;
                  return (
                    <tr
                      key={`${r.crop_id ?? r.display_name ?? 'unknown'}#${idx}`}
                      className="border-b border-border/50 hover:bg-accent/30"
                    >
                      <td className="py-2 px-3">
                        <div
                          className="px-2.5 py-1 rounded inline-block"
                          style={{
                            background: c.bg,
                            borderLeft: `3px solid ${c.border}`,
                          }}
                        >
                          <CropChip
                            name={r.display_name}
                            iconSize={16}
                            className="font-medium"
                            emptyLabel={
                              <span className="text-muted-foreground">
                                не указана
                              </span>
                            }
                          />
                          {r.local_name &&
                            r.crop_name &&
                            r.local_name !== r.crop_name && (
                              <div className="text-xs text-muted-foreground">
                                ≈ {r.crop_name}
                              </div>
                            )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right">
                        {(r.fields_count ?? 0).toLocaleString('ru-RU')}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <span className="text-xs">
                            {r.planned_area_ha > 0
                              ? Math.round(r.planned_area_ha).toLocaleString(
                                  'ru-RU',
                                )
                              : '—'}{' '}
                            /{' '}
                            <strong>
                              {r.actual_area_ha > 0
                                ? Math.round(r.actual_area_ha).toLocaleString(
                                    'ru-RU',
                                  )
                                : '—'}
                            </strong>{' '}
                            га
                          </span>
                          {/* У пара нет «выполнения плана сева»: показываем
                              состояние поля вместо процента. */}
                          {r.is_fallow ? (
                            <Badge variant="outline" className="text-[10px]">
                              Парует
                            </Badge>
                          ) : (
                            planCompletionBadge(
                              r.planned_area_ha,
                              r.actual_area_ha,
                            )
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 min-w-[160px]">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-2 flex-1 rounded-full bg-secondary overflow-hidden"
                            title={`${pct.toFixed(1)}% от ${
                              totalFact > 0 ? 'факт.' : 'плановой'
                            } площади`}
                          >
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${Math.min(100, pct)}%`,
                                background: c.border,
                              }}
                            />
                          </div>
                          <span className="text-xs tabular-nums w-10 text-right">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {/* Лестница сева у пара бессмысленна — счётчики
                              статусов для него не показываем вовсе. */}
                          {!r.is_fallow && r.status_planned > 0 && (
                            <Badge variant="outline" className="text-[10px]">
                              План: {r.status_planned}
                            </Badge>
                          )}
                          {!r.is_fallow && r.status_growing > 0 && (
                            <Badge variant="primary" className="text-[10px]">
                              Растёт: {r.status_growing}
                            </Badge>
                          )}
                          {!r.is_fallow && r.status_harvested > 0 && (
                            <Badge variant="secondary" className="text-[10px]">
                              Убрано: {r.status_harvested}
                            </Badge>
                          )}
                          {!r.is_fallow && r.status_other > 0 && (
                            <Badge
                              variant="destructive"
                              className={cn('text-[10px]')}
                            >
                              Прочее: {r.status_other}
                            </Badge>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default RotationCard;
